import React, { useEffect, useState } from "react";
import { CalendarPlus, FolderPlus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/dashboard/ui";
import CapacityCalendar from "@/components/CapacityCalendar";

export default function DoctorBooking() {
  const [services, setServices] = useState([]);
  const [slot, setSlot] = useState(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [f, setF] = useState({ start: "09:00", end: "12:00", services: [], description: "", recurring: "" });
  const [pf, setPf] = useState({ service: "", description: "" });

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data || [])); }, []);
  const toggle = (t) => setF((s) => ({ ...s, services: s.services.includes(t) ? s.services.filter((x) => x !== t) : [...s.services, t] }));

  const submitSlot = async () => {
    if (f.services.length === 0) { toast.error("Sélectionnez au moins un service"); return; }
    try {
      await api.post("/requests", {
        kind: "time", services: f.services, description: f.description, recurring: f.recurring,
        dates: [{ date: slot.date, start: f.start, end: f.end }],
      });
      toast.success("Votre demande a bien été envoyée. Notre équipe va vérifier les disponibilités et vous confirmer la mission.");
      setSlot(null); setF({ start: "09:00", end: "12:00", services: [], description: "", recurring: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitProject = async () => {
    if (!pf.service) { toast.error("Sélectionnez un service"); return; }
    try {
      await api.post("/requests", { kind: "project", services: [pf.service], description: pf.description, dates: [] });
      toast.success("Votre demande de projet a été transmise à notre équipe.");
      setProjectOpen(false); setPf({ service: "", description: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bookable = services.filter((s) => s.bookable);
  const projectServices = services.filter((s) => s.custom_request);

  return (
    <div>
      <PageHeader title="Réserver une disponibilité" subtitle="Choisissez une date disponible pour envoyer votre demande de mission."
        action={<Button variant="outline" onClick={() => setProjectOpen(true)} data-testid="project-request-btn"><FolderPlus className="h-4 w-4 mr-1.5" />Demande de projet</Button>} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><CapacityCalendar onSelectDay={(d) => setSlot(d)} /></div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-semibold text-slate-900">Comment ça marche</h3>
          <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal list-inside">
            <li>Cliquez sur une journée disponible (vert ou ambre).</li>
            <li>Indiquez vos horaires et services.</li>
            <li>Envoyez votre demande.</li>
          </ol>
          <p className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-100">L'envoi d'une demande ne constitue pas une confirmation. Notre équipe vérifie les disponibilités et confirme la mission.</p>
        </div>
      </div>

      <Dialog open={!!slot} onOpenChange={(o) => !o && setSlot(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-primary" />Demande de mission</DialogTitle>
            <DialogDescription>{slot && new Date(slot.date).toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Début</Label><Input type="time" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1" data-testid="book-start" /></div>
              <div><Label>Fin</Label><Input type="time" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1" data-testid="book-end" /></div>
            </div>
            <div><Label>Services</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto custom-scroll">
                {bookable.map((s) => (
                  <label key={s.service_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={f.services.includes(s.title)} onCheckedChange={() => toggle(s.title)} data-testid={`book-svc-${s.service_id}`} />{s.title}
                  </label>
                ))}
              </div>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Besoin récurrent (facultatif)</Label><Input value={f.recurring} onChange={(e) => setF({ ...f, recurring: e.target.value })} placeholder="Ex : chaque lundi" className="mt-1" /></div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">Merci de ne pas transmettre de données médicales sensibles de patients.</p>
          </div>
          <DialogFooter><Button className="bg-primary" onClick={submitSlot} data-testid="book-submit">Envoyer ma demande</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={projectOpen} onOpenChange={setProjectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Demande de projet / accompagnement</DialogTitle><DialogDescription>Pour les services sans créneau horaire (installation, ouverture de cabinet, etc.)</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Service concerné</Label>
              <select className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={pf.service} onChange={(e) => setPf({ ...pf, service: e.target.value })} data-testid="project-service">
                <option value="">Sélectionner…</option>
                {projectServices.map((s) => <option key={s.service_id} value={s.title}>{s.title}</option>)}
              </select>
            </div>
            <div><Label>Description</Label><Textarea rows={4} value={pf.description} onChange={(e) => setPf({ ...pf, description: e.target.value })} className="mt-1" data-testid="project-description" /></div>
          </div>
          <DialogFooter><Button className="bg-primary" onClick={submitProject} data-testid="project-submit">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
